<div class="container mx-auto">
  <!-- Page Header -->
  <div
    class="flex flex-wrap items-center justify-between mb-6 pb-4 border-b border-base-300"
  >
    <div class="flex-1">
      <h2 class="text-2xl font-bold text-base-content flex items-center gap-2">
        <i class="fa-solid fa-id-card"></i>
        Tutor Profile
      </h2>
      <p class="text-sm text-base-content/60 mt-1">
        This is your public tutor profile as seen by students on the directory.
      </p>
    </div>
    <div class="flex gap-2 mt-2">
      <a
        [routerLink]="['/dashboard/profile/tutor-settings']"
        class="daisy-btn daisy-btn-primary gap-2 shadow-md hover:shadow-lg transition-all"
      >
        <i class="fa-solid fa-pen-to-square"></i>
        Edit Profile
      </a>
    </div>
  </div>

  <!-- Pending Approval Notice -->
  @if (user?.pendingTutorProfileUpdates) {
    <div
      class="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 shadow-sm"
    >
      <i
        class="fa-solid fa-clock-rotate-left text-amber-500 mt-0.5 text-lg"
      ></i>
      <div class="flex-grow">
        <p class="text-sm font-semibold text-amber-800 m-0">
          Profile updates are pending administrator approval
        </p>
        <p class="text-xs text-amber-600 m-0 mt-1">
          You submitted changes on
          <strong>{{
            user?.pendingTutorProfileUpdates?.submittedAt | date: 'medium'
          }}</strong
          >. Until approved by an administrator, your public profile and listing
          will continue to show your previous approved settings.
        </p>
      </div>
      <div class="flex flex-col sm:flex-row gap-2 flex-shrink-0 self-center">
        <button
          type="button"
          (click)="refreshProfileStatus()"
          [disabled]="isRefreshing"
          class="daisy-btn daisy-btn-outline daisy-btn-warning daisy-btn-xs gap-1.5"
        >
          <i
            class="fa-solid fa-arrows-rotate"
            [class.fa-spin]="isRefreshing"
          ></i>
          Refresh
        </button>
        <a
          [routerLink]="['/dashboard/profile/tutor-settings']"
          class="daisy-btn daisy-btn-warning daisy-btn-xs"
        >
          Review Settings
        </a>
      </div>
    </div>
  }

  <div class="flex justify-end mb-2">
    <a
      [routerLink]="['/tutors', displayUser?.slug || displayUser?._id || user?.slug || user?._id]"
      target="_blank"
      class="daisy-btn daisy-btn-outline daisy-btn-sm gap-2"
    >
      <i class="fa-solid fa-arrow-up-right-from-square"></i>
      Public Preview
    </a>
  </div>

  <!-- Incomplete Profile Notice -->
  @if (hasIncompleteTutorProfile) {
    <div
      class="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3"
    >
      <i class="fa-solid fa-triangle-exclamation text-amber-500 mt-0.5"></i>
      <div class="flex-1">
        <p class="text-sm font-semibold text-amber-800 m-0">
          Your tutor profile is incomplete
        </p>
        <p class="text-xs text-amber-600 m-0 mt-1">
          Add subjects, WhatsApp number, and your bio to appear in the tutor
          directory and attract students.
        </p>
      </div>
      <a
        [routerLink]="['/dashboard/profile/tutor-settings']"
        class="daisy-btn daisy-btn-warning daisy-btn-xs flex-shrink-0"
      >
        Complete Now
      </a>
    </div>
  }

  <!-- Profile Preview Card -->
  <div class="bg-slate-50 rounded-2xl overflow-hidden border border-slate-200">
    <!-- Banner -->
    <div class="h-36 md:h-52 w-full bg-slate-200 relative overflow-hidden">
      @if (getBannerUrl(displayUser?.profile?.tutorInfo?.bannerImage)) {
        <img
          [src]="getBannerUrl(displayUser?.profile?.tutorInfo?.bannerImage)"
          [alt]="(displayUser?.profile?.personal?.firstName || '') + ' cover'"
          class="w-full h-full object-cover"
        />
      } @else {
        <div
          class="w-full h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-70 flex items-center justify-center"
        >
          <i class="fa-solid fa-image text-white/40 text-6xl"></i>
        </div>
      }
      <div
        class="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent pointer-events-none"
      ></div>
    </div>

    <!-- Profile Header Card -->
    <div class="px-4 md:px-8 -mt-14 relative z-10 pb-6">
      <div
        class="bg-white rounded-2xl p-5 md:p-7 shadow-sm border border-slate-100 flex flex-col md:flex-row gap-5 items-start md:items-center"
      >
        <!-- Avatar -->
        <div
          class="relative w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-white overflow-hidden shadow-md flex-shrink-0 bg-slate-100"
        >
          @if (getAvatarUrl(displayUser?.profile?.personal?.avatar)) {
            <img
              [src]="getAvatarUrl(displayUser?.profile?.personal?.avatar)"
              [alt]="displayUser?.profile?.personal?.firstName"
              class="w-full h-full object-cover"
            />
          } @else {
            <div
              class="w-full h-full flex items-center justify-center text-slate-300 text-5xl"
            >
              <i class="fa-solid fa-user"></i>
            </div>
          }
        </div>

        <!-- Info -->
        <div class="flex-grow min-w-0">
          <div class="flex items-center gap-2.5 flex-wrap">
            <h1 class="text-2xl md:text-3xl font-extrabold text-slate-900 m-0">
              {{ displayUser?.profile?.personal?.firstName }}
              {{ displayUser?.profile?.personal?.lastName }}
            </h1>
            @if (displayUser?.profile?.tutorInfo?.isVerifiedTutor) {
              <span
                class="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full flex items-center gap-1"
              >
                <i class="fa-solid fa-circle-check text-xs"></i>
                Verified
              </span>
            }
            @if (displayUser?.profile?.tutorInfo?.isPublic === false) {
              <span
                class="bg-slate-100 text-slate-600 border border-slate-200 text-xs font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full flex items-center gap-1"
              >
                <i class="fa-solid fa-eye-slash text-xs"></i>
                Private Profile
              </span>
            } @else {
              <span
                class="bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full flex items-center gap-1"
              >
                <i class="fa-solid fa-globe text-xs"></i>
                Public Profile
              </span>
            }
          </div>
          <p class="text-sm md:text-base text-slate-500 font-semibold m-0 mt-1">
            {{ displayUser?.profile?.professional?.jobTitle || 'Expert Tutor' }}
          </p>
          <div class="flex flex-wrap gap-1.5 mt-3">
            @for (sub of displayUser?.profile?.tutorInfo?.subjects || []; track sub) {
              <span
                class="bg-indigo-50 text-indigo-700 text-xs font-semibold px-2.5 py-0.5 rounded-full"
                >{{ sub }}</span
              >
            }
            @if (!displayUser?.profile?.tutorInfo?.subjects?.length) {
              <span class="text-xs text-slate-400 italic"
                >No subjects configured yet — click Edit Profile to add
                them.</span
              >
            }
          </div>
        </div>
      </div>

      <!-- Details Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-5">
        <!-- About (2 cols) -->
        <div
          class="bg-white rounded-xl p-5 border border-slate-100 shadow-sm lg:col-span-2"
        >
          <h4
            class="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2"
          >
            <i class="fa-solid fa-circle-info text-indigo-400"></i>
            About
          </h4>
          @if (displayUser?.profile?.personal?.biography) {
            <div
              class="text-sm text-slate-600 leading-relaxed m-0 prose max-w-none"
              [innerHTML]="displayUser?.profile?.personal?.biography"
            ></div>
          } @else {
            <p class="text-sm text-slate-500 leading-relaxed m-0">
              No biography added yet. Click "Edit Profile" to add one.
            </p>
          }
        </div>

        <!-- Quick Stats (1 col) -->
        <div
          class="bg-white rounded-xl p-5 border border-slate-100 shadow-sm flex flex-col gap-3"
        >
          <h4
            class="text-sm font-bold text-slate-700 mb-1 flex items-center gap-2"
          >
            <i class="fa-solid fa-chart-bar text-indigo-400"></i>
            Profile Stats
          </h4>
          <div class="flex items-center gap-2 text-sm text-slate-600">
            <i class="fa-solid fa-book text-indigo-300 w-4 text-center"></i>
            <span
              >{{
                displayUser?.profile?.tutorInfo?.subjects?.length || 0
              }}
              Subject(s)</span
            >
          </div>
          <div class="flex items-center gap-2 text-sm text-slate-600">
            <i
              class="fa-solid fa-graduation-cap text-indigo-300 w-4 text-center"
            ></i>
            <span
              >{{
                displayUser?.profile?.tutorInfo?.grades?.length || 0
              }}
              Grade(s)</span
            >
          </div>
          <div class="flex items-center gap-2 text-sm text-slate-600">
            <i
              class="fa-solid fa-location-dot text-indigo-300 w-4 text-center"
            ></i>
            <span
              >{{
                displayUser?.profile?.tutorInfo?.districts?.length || 0
              }}
              District(s) covered</span
            >
          </div>
          <div class="flex items-center gap-2 text-sm text-slate-600">
            <i
              class="fa-brands fa-whatsapp w-4 text-center"
              [class.text-emerald-500]="
                displayUser?.profile?.tutorInfo?.whatsAppNumber
              "
              [class.text-slate-300]="!displayUser?.profile?.tutorInfo?.whatsAppNumber"
            ></i>
            <span>{{
              displayUser?.profile?.tutorInfo?.whatsAppNumber
                ? 'WhatsApp set'
                : 'WhatsApp not set'
            }}</span>
          </div>
          @if (displayUser?.profile?.tutorInfo?.ratePerHour) {
            <div class="flex items-center gap-2 text-sm text-slate-600">
              <i
                class="fa-solid fa-money-bill-wave text-indigo-300 w-4 text-center"
              ></i>
              <span>Rs. {{ displayUser?.profile?.tutorInfo?.ratePerHour }}/hr</span>
            </div>
          }
        </div>
      </div>

      <!-- Credentials & Qualifications -->
      @if (
        displayUser?.profile?.professional?.educationalQualifications?.length ||
        displayUser?.profile?.professional?.certifications?.length ||
        displayUser?.profile?.professional?.experience
      ) {
        <div
          class="bg-white rounded-xl p-5 border border-slate-100 shadow-sm mt-4"
        >
          <h4
            class="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2"
          >
            <i class="fa-solid fa-award text-indigo-400"></i>
            Credentials &amp; Experience
          </h4>
          <div class="flex flex-col gap-4">
            @if (displayUser?.profile?.professional?.experience) {
              <div class="flex gap-3 items-start">
                <span
                  class="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 text-indigo-500"
                >
                  <i class="fa-solid fa-briefcase text-sm"></i>
                </span>
                <div>
                  <h5 class="text-sm font-bold text-slate-800 m-0">
                    Teaching Experience
                  </h5>
                  <p class="text-xs text-slate-500 m-0 mt-0.5">
                    {{ displayUser?.profile?.professional?.experience }}
                  </p>
                </div>
              </div>
            }
            @if (
              displayUser?.profile?.professional?.educationalQualifications?.length
            ) {
              <div class="flex gap-3 items-start">
                <span
                  class="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 text-indigo-500"
                >
                  <i class="fa-solid fa-graduation-cap text-sm"></i>
                </span>
                <div>
                  <h5 class="text-sm font-bold text-slate-800 mb-1">
                    Education
                  </h5>
                  <ul
                    class="list-disc pl-4 text-xs text-slate-500 m-0 flex flex-col gap-1"
                  >
                    @for (
                      qual of displayUser?.profile?.professional
                        ?.educationalQualifications;
                      track qual
                    ) {
                      <li>{{ qual }}</li>
                    }
                  </ul>
                </div>
              </div>
            }
            @if (displayUser?.profile?.professional?.certifications?.length) {
              <div class="flex gap-3 items-start">
                <span
                  class="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 text-indigo-500"
                >
                  <i class="fa-solid fa-certificate text-sm"></i>
                </span>
                <div>
                  <h5 class="text-sm font-bold text-slate-800 mb-1">
                    Certifications
                  </h5>
                  <ul
                    class="list-disc pl-4 text-xs text-slate-500 m-0 flex flex-col gap-1"
                  >
                    @for (
                      cert of displayUser?.profile?.professional?.certifications;
                      track cert
                    ) {
                      <li>{{ cert }}</li>
                    }
                  </ul>
                </div>
              </div>
            }
          </div>
        </div>
      }

      <!-- Class Formats & Coverage -->
      @if (
        displayUser?.profile?.tutorInfo?.classFormats?.length ||
        displayUser?.profile?.tutorInfo?.mediums?.length
      ) {
        <div
          class="bg-white rounded-xl p-5 border border-slate-100 shadow-sm mt-4"
        >
          <h4
            class="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2"
          >
            <i class="fa-solid fa-chalkboard text-indigo-400"></i>
            Teaching Details
          </h4>
          <div class="flex flex-col gap-3">
            @if (displayUser?.profile?.tutorInfo?.classFormats?.length) {
              <div>
                <p
                  class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2"
                >
                  Class Formats
                </p>
                <div class="flex flex-wrap gap-1.5">
                  @for (
                    fmt of displayUser?.profile?.tutorInfo?.classFormats;
                    track fmt
                  ) {
                    <span
                      class="bg-blue-50 text-blue-700 text-xs font-semibold px-2.5 py-0.5 rounded-full"
                      >{{ fmt }}</span
                    >
                  }
                </div>
              </div>
            }
            @if (displayUser?.profile?.tutorInfo?.mediums?.length) {
              <div>
                <p
                  class="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2"
                >
                  Teaching Mediums
                </p>
                <div class="flex flex-wrap gap-1.5">
                  @for (med of displayUser?.profile?.tutorInfo?.mediums; track med) {
                    <span
                      class="bg-violet-50 text-violet-700 text-xs font-semibold px-2.5 py-0.5 rounded-full"
                      >{{ med }}</span
                    >
                  }
                </div>
              </div>
            }
          </div>
        </div>
      }
    </div>
  </div>
</div>
